import React, { useEffect, useMemo, useRef } from "react"; // Import useRef
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TransformedAddress } from "../../../cart/types/cartApiTypes";
import type { TrCountry } from "../../../../types";
import type { TrCity, TrCityWithDistricts } from "../../types/checkoutApiTypes";

// Form data type: state holds city name, city holds district name, postcode optional
type BillingFormData = Omit<TransformedAddress, "postcode" | "state"> & {
  postcode?: string;
  state: string; // State will hold the City Name (e.g., "BURSA")
  city: string; // City will hold the District Name (e.g., "NILÜFER")
};

export interface BillingAddressFormProps {
  initialData?: Partial<TransformedAddress>; // Initial state is WC code
  onSubmit: (apiData: Partial<TransformedAddress>) => void;
  isSubmitting: boolean;
  formId: string;
  currentStep: string;
  useBillingForShipping: boolean;
  onValidityChange: (isValid: boolean) => void;
  onToggleUseBilling: (isChecked: boolean) => void;
  cities?: TrCity[];
  citiesWithDistricts?: TrCityWithDistricts[];
}

// Field name map
const fieldNameMap: { [key in keyof BillingFormData]?: string } = {
  firstName: "checkout.fields.firstName",
  lastName: "checkout.fields.lastName",
  company: "checkout.fields.company",
  address1: "checkout.fields.address1",
  address2: "checkout.fields.address2",
  city: "checkout.fields.city", // District
  state: "checkout.fields.state", // City/Il
  postcode: "checkout.fields.postcode",
  country: "checkout.fields.country",
  email: "checkout.fields.email",
  phone: "checkout.fields.phone",
};

// Helper functions
const findCityNameByCode = (
  code: string | undefined,
  cities: TrCity[] | undefined
): string => {
  if (!code || !cities) return "";
  const city = cities.find((c) => c.wcCode === code);
  return city?.name || "";
};

const findCodeByCityName = (
  name: string | undefined,
  cities: TrCity[] | undefined
): string => {
  if (!name || !cities) return "";
  const city = cities.find((c) => c.name === name);
  return city?.wcCode || "";
};

const BillingAddressForm: React.FC<BillingAddressFormProps> = ({
  initialData = {},
  onSubmit,
  isSubmitting,
  formId,
  currentStep,
  useBillingForShipping,
  onValidityChange,
  onToggleUseBilling,
  cities = [],
  citiesWithDistricts = [],
}) => {
  const { t } = useTranslation();
  const trCountry = window.themeVariables?.trCountry as TrCountry | undefined;

  const sortedCities = useMemo(
    () =>
      [...(cities || [])].sort((a, b) => a.name.localeCompare(b.name, "tr")),
    [cities]
  );

  const initialFormStateValue = useMemo(
    () => findCityNameByCode(initialData.state, cities),
    [initialData.state, cities]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, dirtyFields }, // Destructure dirtyFields
    reset,
    watch,
    setValue,
    getValues, // Add getValues to read current form state
  } = useForm<BillingFormData>({
    mode: "onChange", // Trigger validation on change
    defaultValues: {
      ...initialData,
      state: initialFormStateValue,
      city: initialData.city || "",
      country:
        trCountry && initialData.country === trCountry.wcCode
          ? trCountry.name
          : initialData.country || "",
    },
  });

  const watchedState = watch("state");
  const watchedCity = watch("city");

  // Ref to track if it's the very first run after initial data is available
  const isInitialResetDone = useRef(false);

  const districtOptions = useMemo(() => {
    if (
      !watchedState ||
      !citiesWithDistricts ||
      citiesWithDistricts.length === 0
    ) {
      return [];
    }
    const selectedCityData = citiesWithDistricts.find(
      (c) => c.name === watchedState
    );
    if (!selectedCityData || !selectedCityData.districts) {
      return [];
    }
    return [...selectedCityData.districts].sort((a, b) =>
      a.name.localeCompare(b.name, "tr")
    );
  }, [watchedState, citiesWithDistricts]);

  // --- Effect 1: Reset District when State (City/İl) changes via user interaction ---
  useEffect(() => {
    if (dirtyFields.state) {
      setValue("city", "", { shouldValidate: true, shouldDirty: true });
      console.log(
        `[Effect 1 Billing] City reset due to state change to: ${watchedState}`
      );
    }
  }, [watchedState, setValue, dirtyFields.state]);

  // --- Effect 2: Reset the entire form when initialData changes, BUT NOT if dirty ---
  useEffect(() => {
    // Only proceed if initialData is actually available
    if (!initialData) {
      return;
    }

    // --- Condition to Prevent Reset ---
    // If any field is dirty (user has typed), DO NOT reset based on initialData changes
    // UNLESS it's the very first time we're setting the initial values.
    if (Object.keys(dirtyFields).length > 0 && isInitialResetDone.current) {
      console.log(
        "[Effect 2 Billing] Skipping reset because form is dirty.",
        dirtyFields
      );
      return; // Exit the effect early
    }
    // --- End Condition ---

    // --- Proceed with Reset (if form is not dirty or it's the initial setup) ---
    const transformedStateName = findCityNameByCode(initialData.state, cities);
    const dataToReset: Partial<BillingFormData> = {
      ...initialData,
      state: transformedStateName || "",
      city: initialData.city || "",
      country:
        trCountry && initialData.country === trCountry.wcCode
          ? trCountry.name
          : initialData.country || "",
    };

    // Check if the data to reset is substantially different from current values
    const currentValues = getValues();
    let needsReset = false;
    for (const key in dataToReset) {
      // Ensure keys exist on both objects before comparing
      if (
        Object.prototype.hasOwnProperty.call(dataToReset, key) &&
        dataToReset[key as keyof BillingFormData] !==
          currentValues[key as keyof BillingFormData]
      ) {
        needsReset = true;
        break;
      }
    }

    if (needsReset) {
      reset(dataToReset, { keepDirty: false }); // Use keepDirty: false to reset dirty state as well
      console.log(
        "[Effect 2 Billing] Form reset based on initialData. State:",
        transformedStateName,
        "City:",
        initialData.city
      );
      // Mark initial reset as done *after* the first successful reset
      if (!isInitialResetDone.current) {
        isInitialResetDone.current = true;
      }
    } else {
      // console.log("[Effect 2 Billing] Skipping reset as data hasn't changed.");
      // Still mark initial reset done if we skipped but data was present
      if (!isInitialResetDone.current && initialData) {
        isInitialResetDone.current = true;
      }
    }
  }, [initialData, cities, reset, trCountry, dirtyFields, getValues]); // Add dirtyFields and getValues

  // --- Effect 3: Report form validity to the parent component ---
  useEffect(() => {
    const isStateSelected = !!watchedState;
    const isCitySelected = !!watchedCity;
    const requiredFieldsFilled = !!(
      (
        isStateSelected &&
        isCitySelected &&
        getValues("firstName")?.trim() &&
        getValues("lastName")?.trim() &&
        getValues("address1")?.trim() &&
        getValues("email")?.trim()
      ) // Ensure email check remains
    );

    const overallValidity = isValid && requiredFieldsFilled;

    if (currentStep === "billing") {
      onValidityChange(overallValidity);
      // console.log(
      //   `[Effect 3 Billing] Reporting validity: ${overallValidity} (RHF isValid: ${isValid}, Fields Filled: ${requiredFieldsFilled})`
      // );
    }
  }, [
    isValid,
    watchedState,
    watchedCity,
    currentStep,
    onValidityChange,
    getValues,
  ]);

  // --- Submit Handler ---
  const handleFormSubmit: SubmitHandler<BillingFormData> = (formData) => {
    const stateCodeToSend = findCodeByCityName(formData.state, cities);
    const countryCodeToSend =
      trCountry && formData.country === trCountry.name
        ? trCountry.wcCode
        : trCountry && formData.country === trCountry.wcCode
        ? trCountry.wcCode
        : formData.country;

    const apiData: Partial<TransformedAddress> = {
      ...formData,
      state: stateCodeToSend || formData.state, // Send code if found, else name
      city: formData.city, // Send district name as 'city'
      country: countryCodeToSend, // Send country code
    };
    onSubmit(apiData);
  };

  // --- Checkbox Handler ---
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleUseBilling(e.target.checked);
  };

  // --- Error Rendering Helper ---
  const renderError = (fieldName: keyof BillingFormData) => {
    const error = errors[fieldName];
    if (!error) return null;
    const labelKey = fieldNameMap[fieldName];
    const translatedLabel = labelKey ? t(labelKey) : fieldName;
    let message = "";
    if (error.type === "required") {
      if (fieldName === "state" && !watchedState) {
        message = t("validation.required", { field: translatedLabel });
      } else if (fieldName === "city" && !watchedCity) {
        message = t("validation.required", { field: translatedLabel });
      } else {
        message = t("validation.required", { field: translatedLabel });
      }
    } else if (error.type === "pattern") {
      message = error.message || t("validation.invalidFormat");
    } else if (error.message) {
      message = error.message;
    } else {
      message = t("validation.invalid", { field: translatedLabel });
    }
    return <span className="error-message">{message}</span>;
  };

  // --- JSX Render ---
  return (
    <form
      id={formId}
      onSubmit={handleSubmit(handleFormSubmit)}
      className="checkout-form-step billing-address-form"
      noValidate
    >
      {/* Checkbox */}
      <div className="form-field full-width use-billing-for-shipping">
        <label htmlFor={`${formId}-use-billing-for-shipping`}>
          <input
            type="checkbox"
            id={`${formId}-use-billing-for-shipping`}
            checked={useBillingForShipping}
            onChange={handleCheckboxChange}
            disabled={isSubmitting}
          />
          {t("checkout.useBillingForShipping")}
        </label>
      </div>

      {/* Form Grid */}
      <div className="form-grid">
        {/* First Name */}
        <div className="form-field">
          <label htmlFor={`${formId}-firstName`}>
            {t("checkout.fields.firstName")}
          </label>
          <input
            type="text"
            id={`${formId}-firstName`}
            {...register("firstName", {
              required: t("validation.required", {
                field: t("checkout.fields.firstName"),
              }),
            })}
            disabled={isSubmitting}
            autoComplete="given-name"
            aria-invalid={errors.firstName ? "true" : "false"}
          />
          {renderError("firstName")}
        </div>

        {/* Last Name */}
        <div className="form-field">
          <label htmlFor={`${formId}-lastName`}>
            {t("checkout.fields.lastName")}
          </label>
          <input
            type="text"
            id={`${formId}-lastName`}
            {...register("lastName", {
              required: t("validation.required", {
                field: t("checkout.fields.lastName"),
              }),
            })}
            disabled={isSubmitting}
            autoComplete="family-name"
            aria-invalid={errors.lastName ? "true" : "false"}
          />
          {renderError("lastName")}
        </div>

        {/* Company */}
        <div className="form-field full-width">
          <label htmlFor={`${formId}-company`}>
            {`${t("checkout.fields.company")} ${t("common.optional")}`}
          </label>
          <input
            type="text"
            id={`${formId}-company`}
            {...register("company")}
            disabled={isSubmitting}
            autoComplete="organization"
          />
          {renderError("company")}
        </div>

        {/* Country */}
        <div className="form-field">
          <label htmlFor={`${formId}-country`}>
            {t("checkout.fields.country")}
          </label>
          <input
            type="text"
            id={`${formId}-country`}
            {...register("country", {
              required: t("validation.required", {
                field: t("checkout.fields.country"),
              }),
            })}
            disabled={true}
            autoComplete="country"
            aria-invalid={errors.country ? "true" : "false"}
            placeholder={trCountry?.name || t("checkout.fields.country")}
            className="country-input-fixed"
          />
          {renderError("country")}
        </div>

        {/* State (City/İl) Dropdown */}
        <div className="form-field">
          <label htmlFor={`${formId}-state`}>
            {t("checkout.fields.state")}
          </label>
          <select
            id={`${formId}-state`}
            {...register("state", {
              required: t("validation.required", {
                field: t("checkout.fields.state"),
              }),
            })}
            disabled={isSubmitting}
            autoComplete="address-level1"
            aria-invalid={errors.state ? "true" : "false"}
          >
            <option value="" disabled>
              {t("checkout.fields.selectState", "-- Select City --")}
            </option>
            {sortedCities.map((city) => (
              <option key={city.wcCode} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
          {renderError("state")}
        </div>

        {/* City (District/İlçe) Dropdown */}
        <div className="form-field">
          <label htmlFor={`${formId}-city`}>{t("checkout.fields.city")}</label>
          <select
            id={`${formId}-city`}
            {...register("city", {
              required: t("validation.required", {
                field: t("checkout.fields.city"),
              }),
            })}
            disabled={
              isSubmitting || !watchedState || districtOptions.length === 0
            }
            autoComplete="address-level2"
            aria-invalid={errors.city ? "true" : "false"}
          >
            <option value="" disabled>
              {districtOptions.length === 0 && watchedState
                ? t("checkout.fields.noDistricts", "-- No Districts --")
                : t("checkout.fields.selectDistrict", "-- Select District --")}
            </option>
            {districtOptions.map((district) => (
              <option key={district.id} value={district.name}>
                {district.name}
              </option>
            ))}
          </select>
          {renderError("city")}
        </div>

        {/* Postcode */}
        <div className="form-field">
          <label htmlFor={`${formId}-postcode`}>
            {`${t("checkout.fields.postcode")} ${t("common.optional")}`}
          </label>
          <input
            type="text"
            id={`${formId}-postcode`}
            {...register("postcode")}
            disabled={isSubmitting}
            autoComplete="postal-code"
            aria-invalid={errors.postcode ? "true" : "false"}
          />
          {renderError("postcode")}
        </div>

        {/* Address 1 */}
        <div className="form-field full-width">
          <label htmlFor={`${formId}-address1`}>
            {t("checkout.fields.address1")}
          </label>
          <input
            type="text"
            id={`${formId}-address1`}
            {...register("address1", {
              required: t("validation.required", {
                field: t("checkout.fields.address1"),
              }),
            })}
            disabled={isSubmitting}
            placeholder={t("checkout.fields.address1Placeholder")}
            autoComplete="address-line1"
            aria-invalid={errors.address1 ? "true" : "false"}
          />
          {renderError("address1")}
        </div>

        {/* Address 2 */}
        <div className="form-field full-width">
          <label htmlFor={`${formId}-address2`}>
            {`${t("checkout.fields.address2")} ${t("common.optional")}`}
          </label>
          <input
            type="text"
            id={`${formId}-address2`}
            {...register("address2")}
            disabled={isSubmitting}
            placeholder={t("checkout.fields.address2Placeholder")}
            autoComplete="address-line2"
          />
          {renderError("address2")}
        </div>

        {/* Email */}
        <div className="form-field full-width">
          <label htmlFor={`${formId}-email`}>
            {t("checkout.fields.email")}
          </label>
          <input
            type="email"
            id={`${formId}-email`}
            {...register("email", {
              required: t("validation.required", {
                field: t("checkout.fields.email"),
              }),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t("validation.emailInvalid"),
              },
            })}
            disabled={isSubmitting}
            autoComplete="email"
            aria-invalid={errors.email ? "true" : "false"}
          />
          {renderError("email")}
        </div>

        {/* Phone */}
        <div className="form-field full-width">
          <label htmlFor={`${formId}-phone`}>
            {`${t("checkout.fields.phone")} ${t("common.optional")}`}
          </label>
          <input
            type="tel"
            id={`${formId}-phone`}
            {...register("phone")}
            disabled={isSubmitting}
            autoComplete="tel"
            aria-invalid={errors.phone ? "true" : "false"}
          />
          {renderError("phone")}
        </div>
      </div>
    </form>
  );
};

export default BillingAddressForm;
